"use client";

import Image from "next/image";
import React, { useMemo } from "react";

/**
 * Academic Deliverables 1031 — BDU "Hands-On Learning with Expert Faculty".
 *
 * Port of BDU v1 `components/Home/HandsOnLearning/`: a full-bleed background image
 * under a flat tint, heading on the left and a stack of icon / title / description
 * rows on the right, separated by thin rules.
 *
 * BREAKPOINTS — v1 `md:` (768px) is `tab:` here.
 *
 * The reference tints with `bg-brown-100 opacity-85`, where `brown-100` is
 * #13344FD4 — despite the name it is the dark blue used across the site, and the
 * token already carries its own alpha before the extra 85% opacity is applied.
 */

interface ExposureRow {
  id?: string | number;
  meta_icon?: string;
  meta_icon_alt?: string;
  meta_title?: string;
  meta_description?: string;
  meta_title_color?: string;
  meta_description_color?: string;
  meta_priority?: number;
  meta_status?: boolean;
}

interface AcademicDeliverables1031Data {
  metadata?: ExposureRow[];
  title?: string;
  title_text_color?: string;
  desktop_bg_image?: string;
  mobile_bg_image?: string;
  alt_image?: string;
  /** Flat tint over the background image */
  section_background_color?: string;
  /** Rule between rows */
  sub_title_text_color?: string;
  status?: boolean;
}

const AcademicDeliverables1031 = ({ data }: { data: any }) => {
  const sectionData = (data?.data ?? data) as
    | AcademicDeliverables1031Data
    | undefined;

  const {
    title = "",
    title_text_color = "#FFFFFF",
    desktop_bg_image = "",
    mobile_bg_image = "",
    section_background_color = "#13344FD4",
    sub_title_text_color = "#A4A4A4",
  } = sectionData || {};

  const rows = useMemo(() => {
    const list = sectionData?.metadata;
    if (!Array.isArray(list)) return [];
    return list
      .filter((r) => r?.meta_status !== false)
      .sort(
        (a, b) =>
          (Number(a?.meta_priority) || 0) - (Number(b?.meta_priority) || 0),
      );
  }, [sectionData?.metadata]);

  if (!sectionData?.status || rows.length === 0) return null;

  const bg = desktop_bg_image || mobile_bg_image || "";

  return (
    <div
      className="relative flex w-full items-center justify-center bg-cover bg-center bg-no-repeat px-[37px] py-[70px] tab:!px-10 tab:py-16"
      style={bg ? { backgroundImage: `url('${bg}')` } : undefined}
    >
      <div
        className="absolute inset-0 size-full opacity-85"
        style={{ backgroundColor: section_background_color }}
      />
      <div className="relative z-10 flex h-full flex-col items-center gap-9 tab:flex-row tab:justify-between tab:gap-24">
        {/* The reference renders this through `Typography asChild`, which emits an h2. */}
        <h2
          className="text-center text-2xl/normal font-bold tab:w-[353px] tab:flex-1 tab:text-left tab:!text-[28px]/normal"
          style={{ color: title_text_color }}
        >
          {title}
        </h2>

        <div className="flex h-full flex-col gap-7 tab:flex-[2]">
          {rows.map((item, index) => (
            <React.Fragment key={item.id ?? index}>
              <div className="flex w-full items-center justify-start gap-[18px] tab:gap-[26px]">
                <div className="flex size-9 items-center tab:size-10">
                  {item.meta_icon && (
                    <Image
                      src={item.meta_icon}
                      alt={item.meta_icon_alt || "Icon"}
                      width={34}
                      height={32}
                      loading="lazy"
                    />
                  )}
                </div>
                <div className="grid flex-1 grid-cols-1 gap-2 tab:grid-cols-[1fr_1.5fr] tab:gap-[26px]">
                  <p
                    className="text-sm font-bold tab:text-base"
                    style={{ color: item.meta_title_color || "#F4F217" }}
                  >
                    {item.meta_title || ""}
                  </p>
                  <p
                    className="w-[207px] overflow-hidden text-ellipsis text-sm font-normal tab:w-[290px] tab:text-base"
                    style={{ color: item.meta_description_color || "#FFFFFF" }}
                  >
                    {item.meta_description || ""}
                  </p>
                </div>
              </div>

              {index < rows.length - 1 && (
                <div
                  className="h-[2px] w-[calc(100%-24px)] tab:w-[calc(100%-64px)]"
                  style={{ backgroundColor: sub_title_text_color }}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AcademicDeliverables1031;
